import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import type { VariantType } from 'notistack';
import { SkinStylesMap } from '../../hooks/useSkinStyles';
import { SKIN } from '../../types/enums';

type SnackbarVariantStyleMap = Record<VariantType, SxProps<Theme>>;

export type StyledSnackbarSkinStyles = {
  root: SxProps<Theme>;
  card: SxProps<Theme>;
  icon: SxProps<Theme>;
  message: SxProps<Theme>;
  actionArea: SxProps<Theme>;
  closeButton: SxProps<Theme>;
  closeIcon: SxProps<Theme>;
  variantCard: SnackbarVariantStyleMap;
  variantIcon: SnackbarVariantStyleMap;
};

const EMPTY_VARIANT_STYLES: SnackbarVariantStyleMap = {
  default: {},
  success: {},
  warning: {},
  error: {},
  info: {},
};

const DEFAULT_STYLES: StyledSnackbarSkinStyles = {
  root: {
    cursor: 'pointer',
  },
  card: {},
  icon: {},
  message: {},
  actionArea: {},
  closeButton: {},
  closeIcon: {},
  variantCard: EMPTY_VARIANT_STYLES,
  variantIcon: EMPTY_VARIANT_STYLES,
};

const NEOBET_VARIANT_ICON_STYLES: SnackbarVariantStyleMap = {
  default: {
    color: 'neutral.200',
  },
  success: {
    color: 'success.400',
  },
  warning: {
    color: 'primary.main',
  },
  error: {
    color: 'error.400',
  },
  info: {
    color: 'info.400',
  },
};

const NEOBET_VARIANT_CARD_STYLES: SnackbarVariantStyleMap = {
  default: {
    borderColor: 'neutral.100',
  },
  success: {
    borderColor: 'success.400',
  },
  warning: {
    borderColor: 'primary.main',
  },
  error: {
    borderColor: 'error.400',
  },
  info: {
    borderColor: 'info.400',
  },
};

const NEOBET_STYLES: StyledSnackbarSkinStyles = {
  root: {
    width: '100%',
    cursor: 'pointer',
  },
  card: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1, md: 1.25 },
    width: { xs: 'calc(100vw - 32px)', md: 420 },
    maxWidth: '100%',
    minHeight: 56,
    px: 2,
    py: 1.25,
    overflow: 'hidden',
    border: '2px solid',
    borderLeftWidth: 6,
    borderRadius: 3,
    color: 'neutral.25',
    backgroundColor: 'neutral.700',
    backgroundImage: (theme) =>
      `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.04)} 0%, transparent 100%)`,
    boxShadow: (theme) => `0 18px 40px ${alpha(theme.palette.common.black, 0.36)}`,
  },
  icon: {
    flexShrink: 0,
    width: 22,
    height: 22,
    '& svg': {
      display: 'block',
      fontSize: 22,
    },
  },
  message: {
    flex: 1,
    minWidth: 0,
    typography: { xs: 'body3', md: 'body2Compact' },
    fontWeight: { xs: 700, md: 700 },
    letterSpacing: { xs: 0, md: 0 },
    color: 'neutral.50',
    overflowWrap: 'anywhere',
  },
  actionArea: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    '& .MuiButton-root': {
      minWidth: 0,
      height: 32,
      px: 1.5,
      py: 0.5,
      borderRadius: 2,
      typography: 'body3',
      fontWeight: 800,
      color: 'neutral.900',
      backgroundColor: 'primary.main',
      '&:hover': {
        backgroundColor: 'primary.600',
      },
    },
  },
  closeButton: {
    flexShrink: 0,
    width: 22,
    height: 22,
    color: 'neutral.200',
    '&.MuiIconButton-root:hover': {
      color: 'neutral.25',
    },
  },
  closeIcon: {
    fontSize: 18,
  },
  variantCard: NEOBET_VARIANT_CARD_STYLES,
  variantIcon: NEOBET_VARIANT_ICON_STYLES,
};

export const STYLED_SNACKBAR_SKIN_STYLES: SkinStylesMap<StyledSnackbarSkinStyles> = {
  [SKIN.BET365]: DEFAULT_STYLES,
  [SKIN.GOLA]: DEFAULT_STYLES,
  [SKIN.NEOBET]: NEOBET_STYLES,
};
